import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next/';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';

import SmartInput from 'src/SmartInput/SmartInput';
import Icon from 'src/components/Icon/Icon';
import { RootState } from 'src/configs/store';
import UseActiveElement from 'src/hooks/useActiveElement';
import DraggableDialog from 'src/components/DraggableDialog/DraggableDialog';
import TypeCodes from '../TypeCodesModal/TypeCodes';
import FastModeHistory from 'src/components/NumbersBetting/FastModeHistory/FastModeHistorySports';

interface SportFasteModeProps {
  coupon?: number,
  card?: string,
}

function SportsFastMode({ coupon=null, card=null } : SportFasteModeProps) {
  const [showModal, setShowModal] = useState(false);
  const [focus, setFocus]= useState('cardCodes');
  const [cardCodes, setCardCodes] = useState([]);
  const [betCodes, setBetCodes] = useState([]);
  const [marketDefaultValue, setMarketDefaultValue] = useState('');
  const [shouldScroll, setShouldScroll] = useState(false);
  const [stake, setStake] = useState('');
  const [defaultValue, setDefaultValue] = useState('');
  const { retailErrors } = useSelector((state: RootState) => state.racing);
  const { bets }= useSelector((state:RootState) => state.betslip);
  const {
    sportErrors,
    sportValues
  } = useSelector((state: RootState) => state.events);

  const firstFocus = useRef(null);
  const lastFocus = useRef(null);
  const stakeRef = useRef(null);

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { typeOfCode } = useSelector((state: RootState) => state.peripherals);
  const focusedElement = UseActiveElement();

  const getCodes = async () => {
    const eventCodes = await dispatch.events.getEventCodes(1);
    const marketCodes = await dispatch.events.getBetCodes(1);
    if (eventCodes){
      setCardCodes(eventCodes);
    }

    if (marketCodes){
      setBetCodes(marketCodes);
    }
  };

  useEffect(() => {
    getCodes();
  }, []);

  useEffect(() => {
    if (coupon || card){
      const event = _.find(cardCodes, { [typeOfCode === 'cardCode' ? 'couponCode' : 'couponCardCode']: typeOfCode === 'cardCode' ? coupon : card });

      if (event){
        dispatch.events.changeValues({ key: 'betCode', value: typeOfCode === 'cardCode' ? card.toString() : coupon.toString() });
        setDefaultValue(`${ typeOfCode === 'cardCode' ? card : coupon } - ${ event.name }`);
      }
    }
    return (() =>  dispatch.events.changeValues({ key: 'betCode', value: '' }));
  }, [coupon, card, cardCodes]);

  const handleClick = (focusValue) => {
    setFocus(focusValue);
    setShowModal(!showModal);
  };

  const checkOptions = (type) => {
    if (type === 'eventCodes'){
      const newCodes = _.map(cardCodes, (codes) => {
        if (typeOfCode === 'cardCode'){
          return { code: (codes.couponCardCode).toString(), name: codes.nextEventName };
        } else {
          return { code: (codes.couponCode).toString(), name: codes.nextEventName };
        }
      });
      return newCodes;
    } else if (type === 'marketCodes'){
      const newCodes = _.map(betCodes, (codes) => {
        if (typeOfCode === 'cardCode'){
          return { code: (codes.betCardCode).toString(), name: `${ codes.market_type.name } - ${ codes.name }` };
        } else {
          return { code: (codes.betCode).toString(), name: `${ codes.market_type.name } - ${ codes.name }` };
        }
      });
      return newCodes;
    }
    return null;
  };

  const haveEmptyFields = () => {
    return _.isEmpty(sportValues.betCode)
      ||  _.isEmpty(sportValues.betType);
  };

  const handleStake = async (e) => {
    setStake(e.target.value);
    await dispatch.events.changeValues({ key: e.target?.name, value: e.target.value });

    if (retailErrors[e.target?.name] !== ''){
      dispatch.events.changeErrors({ value:'', error: '' });
    }
  };

  const handleSportsSubmit = async (e=null) => {
    if (e){
      e.preventDefault();
    }

    const eventInfo = await dispatch.events.searchBetCode({
      code:sportValues.betCode, productType: 1,
      betCode: sportValues.betType });

    if (eventInfo && _.size(eventInfo) > 1){
      const market = _.find(checkOptions('marketCodes'), { code: sportValues.betType });
      const bets = _.find(checkOptions('eventCodes'), { code: sportValues.betCode +'' });
      await dispatch.betslip.addBet({ ...eventInfo, fixtureCode: sportValues.betCode, betCode: sportValues.betType, stake });
      await dispatch.events.addToFastInputHistory({
        marketName: `${ market.code } - ${ market.name }`,
        datetime: eventInfo.datetime,
        eventName: `${ bets?.code } - ${ bets.name }`,
        eventCode: sportValues.betCode,
        marketCode: sportValues.betType
      });
    } else if (eventInfo && _.size(eventInfo) === 1){
      dispatch.events.changeErrors({ value:'betType', error:t('invalidOutcome') });
    } else {
      dispatch.events.changeErrors({ value:'betcode', error:t('invalidFixture') });
    }
  };

  const SmartInputSelect = useCallback((value, type) => {
    dispatch.events.changeValues({ key: type, value });
  }, [dispatch.events.changeValues]);

  const changeFocus = useCallback(() => {
    setShowModal(false);
    if (focus === 'cardCodes'){
      setFocus('betCodes');
    } else {
      setFocus('cardCodes');
    }
    setShowModal(true);
  }, [focus]);

  const clearAllValues = (e) => {
    e.preventDefault();
    dispatch.events.resetRetailValues();
    dispatch.lotto.resetRetailValues();
    setMarketDefaultValue('');
    setDefaultValue('');
  };

  const tabFunction = useCallback((e) => {
    if (e.key === '+'){
      e.preventDefault();
      handleSportsSubmit(e);
    }

    if (e.key === '*'){
      e.preventDefault();
      dispatch.events.resetRetailValues();
      dispatch.events.resetLottoFastModeHistory();
      dispatch.lotto.activateResetNumbers();
    }

    if (e.key === '/'){
      e.preventDefault();
      setMarketDefaultValue('');
      setDefaultValue('/');
      dispatch.events.changeValues({ key: 'betType', value: '' });
    }

    if (e.key === 'Enter' && focusedElement.className.includes('focusable')){
      e.preventDefault();
      stakeRef.current.focus();
      if (stakeRef.current.value !== ''){
        stakeRef.current.select();
      }
    }

    if (e.key === 'I' && e.shiftKey){
      e.preventDefault();
      if (!showModal){
        setShowModal(true);
      }
    } else if (e.key === 'F4'){

      if (!_.isEqual(bets, [])){
        dispatch.betslip.submitBets(true);
      }
    } else if ((e.key === 'R' || e.key === 'r') && e.shiftKey){
      e.preventDefault();
    } else if ((e.key === 'ArrowRight')){
      const inputs = document.getElementsByClassName('focusable');
      const selectedIndex = _.findIndex(inputs, (input) => input === focusedElement);

      if (selectedIndex >= 0){
        if (inputs[selectedIndex +1]){
          (inputs[selectedIndex +1] as HTMLElement)?.focus();
        } else {
          (inputs[0] as HTMLElement)?.focus();
        }
      } else if (document.activeElement.className.includes('--stake')){
        (inputs[inputs.length -1] as HTMLElement)?.focus();
      }
    } else if (e.key === 'ArrowLeft'){
      const inputs = document.getElementsByClassName('focusable');
      const selectedIndex = _.findIndex(inputs, (input) => input === focusedElement);

      if (selectedIndex >= 0){
        if (inputs[selectedIndex -1]){
          (inputs[selectedIndex -1] as HTMLElement)?.focus();
        } else {
          (inputs[inputs.length -1] as HTMLElement)?.focus();
        }
      } else if (document.activeElement.className.includes('--stake')){
        (inputs[1] as HTMLElement)?.focus();
      }
    }

  }, [bets, focusedElement, stake]);

  useEffect(() => {
    addEventListener('keydown', tabFunction);
    return () => {
      removeEventListener('keydown', tabFunction);
    };
  }, [bets, focusedElement, stake]);

  const getTitle = () => {
    let title = t('typeCodes.cardCodesSportTitle');

    if (focus === 'betCodes'){
      title = t('typeCodes.betCodesSportTitle');
    }

    return title;
  };

  const changeMarketDefault = useCallback((value) => {
    const newBetCode = value.split(' - ');
    dispatch.events.changeValues({ key: 'betType', value: newBetCode[0] });

    setMarketDefaultValue(value);
  }, [dispatch.events.changeValues]);

  const changeEventDefault = useCallback((value) => {

    const newBetCode = value.split(' - ');

    dispatch.events.changeValues({ key: 'betCode', value: newBetCode[0] });
    setDefaultValue(value);
  }, [dispatch.events.changeValues]);

  return (
    <>
      <form autoComplete="off" onBlur={ () => setShouldScroll(true) } onFocus={ () => setShouldScroll(false) }>
        <div className="fast-mode__form">
          <div className="fast-mode__label fast-mode__underline" onClick={ () => handleClick('cardCodes') }>
            { t('betCodeSearch.cardCode') }
          </div>
          <div className="fast-mode__label fast-mode__underline" onClick={ () => handleClick('betCodes') }>
            { t('racingDetail.retailBetCodeLabel') }
          </div>
          <div className="fast-mode__label">
            { t('racingDetail.retailStake') }
          </div>
          <div />
          <SmartInput
            options={ checkOptions('eventCodes') }
            onSelect={ SmartInputSelect }
            className="fast-mode__search-input focusable fast-mode__search-input--cardCodes"
            ref={ firstFocus }
            name="betCode"
            onFocus={ () => setFocus('cardCodes') }
            placeHolder={ t(`typeOfCode.${ [typeOfCode] }`) }
            defaultValue={ defaultValue }
            isFocused={ focus === 'cardCodes' && !shouldScroll }
            shouldOpen={ (focusedElement.className).includes('fast-mode__search-input--cardCodes') }
          />
          <SmartInput
            options={ checkOptions('marketCodes') }
            onSelect={ SmartInputSelect }
            className="fast-mode__search-input focusable fast-mode__search-input--betcodes"
            name="betType"
            ref={ lastFocus }
            onFocus={ () => setFocus('betCodes') }
            placeHolder={ typeOfCode === 'cardCode' ? t('numbers.betCodePlaceholder') : t('numbers.betCouponCodePlaceholder') }
            defaultValue={ marketDefaultValue }
            isFocused={ focus === 'betCodes' }
            shouldOpen={ (focusedElement.className).includes('fast-mode__search-input--betcodes') && !shouldScroll }
          />
          <input
            className="fast-mode__input fast-mode__input--stake"
            name="stake"
            ref={ stakeRef }
            value={ stake }
            onChange={ handleStake }
          />
          <button
            type="button"
            onClick={ handleSportsSubmit }
            className={
              classNames({
                'fast-mode__button' : true,
                'fast-mode__button--disabled': haveEmptyFields()
              })
            }
            disabled = { haveEmptyFields() }
          >
            <Icon
              icon="return"
              className={
                classNames({
                  'fast-mode__check': true,
                  'fast-mode__check--disabled': haveEmptyFields()
                })
              }
            />
          </button>
        </div>
        <div className="number-input-markets">
          <div className="number-input-markets__markets-container">
            <span />
          </div>
          <div className="number-input-markets__buttons-container">
            <button
              className="fast-mode__clear"
              onClick={ clearAllValues }
            > { t('numbers.clearAll') }</button>
          </div>
        </div>
        {
          _.some(sportErrors, (error) => error !== '') &&
          <div className="fast-mode__errors-container">
            <span className="fast-mode__error">
              { `${ sportErrors.betcode || '' } ${ sportErrors.betType || '' } ${ sportErrors.numbers || '' } ${ sportErrors.stake || '' }` }
            </span>
          </div>
        }
        <FastModeHistory smartInputSelect={ changeMarketDefault } changeLastValues={ changeEventDefault } />
      </form>
      {
        showModal &&
        <DraggableDialog className="fast-mode__dialog" placement="right-start">
          <DraggableDialog.Content>
            <TypeCodes
              closeModal = { () => setShowModal(false) }
              title= { getTitle() }
              changeFocus= { changeFocus }
            >
              { focus === 'cardCodes' && <TypeCodes.CardCodes productType={ 1 } /> }
              { (focus !== 'cardCodes') && <TypeCodes.SportMarket /> }
            </TypeCodes>
          </DraggableDialog.Content>
        </DraggableDialog>
      }
    </>
  );
}

export default SportsFastMode;
